import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled from 'styled-components';

import { blueLink, navy, grey10, white } from '@zocdoc/branding/colors';
import { Link } from '../link';
import { breakpoint } from '../grid';
import { FontFamily } from '../typography';
import CollapsibleSection, { CollapsibleSectionPrimitives } from '../CollapsibleSection';
import isMobileOrTabletBreakpoint from '../utils/isMobileOrTabletBreakpoint';
import { PAGE_X_AXIS_PADDING_BY_BREAKPOINT } from '../Layout';

class BaseSeoRelatedLinks extends PureComponent {
    static propTypes = {
        sections: PropTypes.arrayOf(
            PropTypes.shape({
                title: PropTypes.string.isRequired,
                links: PropTypes.arrayOf(
                    PropTypes.shape({
                        url: PropTypes.string.isRequired,
                        text: PropTypes.string.isRequired,
                        tooltip: PropTypes.string,
                    })
                ),
            })
        ),
    };

    constructor(props) {
        super(props);
        this.state = {
            isClient: false,
        };
    }

    componentDidMount() {
        this.setState({ isClient: true });
    }

    render() {
        const { sections, ...props } = this.props;

        return (
            <div {...props}>
                {this.renderSectionLinks(sections)}
            </div>
        );
    }

    renderSectionLinks = sections => {
        if (isMobileOrTabletBreakpoint() && this.state.isClient) {
            return sections.map(this.renderSectionMobile);
        } else {
            return sections.map(this.renderSectionDesktop);
        }
    }

    renderSectionDesktop = (section, __, sections) => {
        return (
            <Column
                key={section.title}
                data-test="seo-related-links-desktop"
                sections={sections}
            >
                <Header title={section.title}>{section.title}</Header>
                <ul>
                    {section.links.map(this.renderLink)}
                </ul>
            </Column>
        );
    }

    renderSectionMobile = section => {
        return (
            <LinkFarmCollapsibleSection
                key={section.title}
                autoscroll
                data-test="seo-related-links-mobile"
                headerOpenText={section.title}
                headerText={section.title}
            >
                <ul>
                    {section.links.map(this.renderLink)}
                </ul>
            </LinkFarmCollapsibleSection>
        );
    }

    renderLink(link) {
        return (
            <li key={[link.url, link.text].join('__')}>
                <RelatedLink
                    href={link.url}
                    title={link.tooltip}
                >
                    {link.text}
                </RelatedLink>
            </li>
        );
    }
}

const SeoRelatedLinks = styled(BaseSeoRelatedLinks)`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    ${breakpoint.medium} {
        padding-top: 40px;
        padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.MEDIUM}px;
        padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.MEDIUM}px;
    }

    ${breakpoint.large} {
        padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.LARGE}px;
        padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.LARGE}px;
    }

    ${breakpoint.xlarge} {
        padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XLARGE}px;
        padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XLARGE}px;
    }
    ${breakpoint.xxlarge} {
        padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXLARGE}px;
        padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXLARGE}px;
    }
`;

const RelatedLink = styled(Link)`
    font: 13px/28px ${FontFamily.SharpSansSemibold};
    color: ${blueLink};

    ${breakpoint.small} {
        font-size: 15px;
    }
`;

const Column = styled.div`
    box-sizing: border-box;
    margin-bottom: 32px;
    width: 50%;
    padding-right: 10%;
    
    ${breakpoint.medium} {
        width: 33%;
        padding-right: 5%;
    }
`;

const Header = styled.h3`
    padding-bottom: 9px;
    font: 16px/20px ${FontFamily.SharpSansBold};
    border-bottom: 2px solid ${navy};
    margin-bottom: 20px;
`;

const LinkFarmCollapsibleSection = styled(CollapsibleSection)`
    display: block;
    width: 100%;

    ${CollapsibleSectionPrimitives.Header} {
        background-color: ${grey10};
        font: 15px/16px ${FontFamily.SharpSansSemibold};
        margin-top: 1px;
        padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXSMALL}px;
        padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXSMALL}px;

        ${breakpoint.xsmall} {
            padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XSMALL}px;
            padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XSMALL}px;
        }

        ${breakpoint.small} {
            padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.SMALL}px;
            padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.SMALL}px;
        }

        ${breakpoint.medium} {
            padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.MEDIUM}px;
            padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.MEDIUM}px;
        }

        ${CollapsibleSectionPrimitives.StyledArrow} {
            font-size: 8px;
        }
    }

    ${CollapsibleSectionPrimitives.Children} {
        background-color: ${grey10};
        border-top: 1px solid ${white};
        margin-top: 0;
        padding: 30px ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XXSMALL}px 30px;

        ${breakpoint.xsmall} {
            padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XSMALL}px;
            padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.XSMALL}px;
        }

        ${breakpoint.small} {
            padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.SMALL}px;
            padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.SMALL}px;
        }

        ${breakpoint.medium} {
            padding-left: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.MEDIUM}px;
            padding-right: ${PAGE_X_AXIS_PADDING_BY_BREAKPOINT.MEDIUM}px;
        }

        &::before {
            display: none;
        }
    }
`;

export { SeoRelatedLinks };
export const primitives = {
    Column,
    Header,
    RelatedLink,
};
